import { useEffect, type ReactNode } from "react";
import { applyTheme, getStoredTheme } from "@/lib/theme";
import { MeshBackground } from "@/components/marketing/MeshBackground";
import { useTransparencyConfig } from "@/hooks/useTransparencyConfig";
import "@/styles/marketing.css";

/**
 * Applies the marketing/public-surface visual scope WITHOUT touching the user's
 * saved in-app theme preference.
 *
 * - Forces light mode while mounted (the soft-frosted palette lives in light).
 * - Sets `data-marketing="true"` on <html>, which activates the scoped
 *   token + typography + glass overrides in `src/styles/marketing.css`.
 * - Renders the animated mesh gradient behind all content.
 * - On unmount (navigating into the authenticated app), restores the user's
 *   theme and clears the marketing attribute.
 *
 * See mem://design/knowledgeworker-io-design.md.
 */
export function MarketingThemeScope({ children }: { children: ReactNode }) {
  useTransparencyConfig(); // loads admin-configured transparency and applies CSS vars
  useEffect(() => {
    const html = document.documentElement;
    const wasDark = html.classList.contains("dark");
    html.classList.remove("dark");
    html.dataset.marketing = "true";
    return () => {
      delete html.dataset.marketing;
      // Restore whatever the user actually has saved (or re-apply dark).
      if (wasDark) {
        html.classList.add("dark");
      } else {
        applyTheme(getStoredTheme());
      }
    };
  }, []);

  return (
    <>
      <MeshBackground />
      {children}
    </>
  );
}
